import { z } from 'zod'

import { APPLICATION_STATUS, APPLICATION_SYNC_STATUS, CLIENT_DOCUMENT_STATUS } from '../constants'

import { clientFileSchema } from './file.schema'
import { getPaginationDataSchema } from './common.schema'
import { invoiceStatusSchema, invoiceFileSchema } from './invoice.schema'

export const clientApplicationOverviewSchema = z.object({
  total: z.number(),
  working: z.number(),
  created: z.number(),
  approved: z.number(),
  inProgress: z.number(),
  documentsRequired: z.number().nullable(),
  // Percentage changes compared to previous period (only for staff view)
  totalChange: z.number().nullable().optional(),
  approvedChange: z.number().nullable().optional(),
  createdChange: z.number().nullable().optional(),
  inProgressChange: z.number().nullable().optional(),
  workingChange: z.number().nullable().optional(),
})

export type TClientApplicationOverview = z.infer<typeof clientApplicationOverviewSchema>

export const clientApplicationProfileSchema = z.object({
  id: z.number(),
  city: z.string(),
  email: z.string().nullable(),
  gender: z.string(),
  country: z.string(),
  lastName: z.string(),
  firstName: z.string(),
  dateOfBirth: z.string(),
  citizenship: z.string(),
  phoneNumber: z.string(),
  phoneCountryIso: z.string().nullable(),
  createdDate: z.string(),
  applicationId: z.number(),
  streetNumberAndName: z.string(),
  stateProvinceRegion: z.string().nullable(),
  zipPostalCode: z.string().nullable(),
  middleName: z.string().optional().nullable(),
  updatedDate: z.string().optional().nullable(),
  unitApartment: z.string().optional().nullable(),
  maidenNameAlias: z.string().optional().nullable(),
})

export type TClientApplicationProfile = z.infer<typeof clientApplicationProfileSchema>

export const clientApplicationStatus = z.enum([
  APPLICATION_STATUS.PREPARING,
  APPLICATION_STATUS.APPROVED,
  APPLICATION_STATUS.VERIFYING,
  APPLICATION_STATUS.COMPLETED,
  APPLICATION_STATUS.APOSTILLE,
  APPLICATION_STATUS.MAILING,
  APPLICATION_STATUS.CREATED,
  APPLICATION_STATUS.IN_PROGRESS,
  APPLICATION_STATUS.SUBMITTED,
])

export type TClientApplicationStatus = z.infer<typeof clientApplicationStatus>

export const clientApplicationSyncStatusSchema = z.enum([
  APPLICATION_SYNC_STATUS.NOT_SYNCED,
  APPLICATION_SYNC_STATUS.ERROR,
  APPLICATION_SYNC_STATUS.SYNCED,
  APPLICATION_SYNC_STATUS.SYNCING,
])

export type TClientApplicationSyncStatus = z.infer<typeof clientApplicationSyncStatusSchema>

export const applicationDetailClientSchema = z.object({
  id: z.string(),
  userName: z.string(),
  email: z.string(),
  phoneNumber: z.string().nullable(),
  active: z.boolean(),
  fullName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  lastActive: z.string().nullable(),
})

export type TApplicationDetailClient = z.infer<typeof applicationDetailClientSchema>

export const applicationDetailInvoiceSchema = z.object({
  id: z.number(),
  applicationId: z.number(),
  applicationName: z.string(),
  amount: z.number(),
  paidAt: z.string().nullable(),
  status: invoiceStatusSchema,
  invoiceFiles: z.array(invoiceFileSchema).optional(),
})

export type TApplicationDetailInvoice = z.infer<typeof applicationDetailInvoiceSchema>

export const clientApplicationDetailSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    reason: z.string(),
    clientId: z.string(),
    typeCode: z.string(),
    typeName: z.string(),
    startDate: z.string(),
    createdAt: z.string(),
    clientName: z.string(),
    hearAboutUsFrom: z.string(),
    termOfAgreement: z.boolean(),
    apostilleService: z.boolean(),
    documentsRequired: z.number(),
    documentsUploaded: z.number(),
    status: clientApplicationStatus,
    typeOther: z.string().nullable(),
    updatedAt: z.string().optional().nullable(),
    apostilleCountryName: z.string().nullable(),
    apostilleCountryCode: z.string().nullable(),
    syncStatus: clientApplicationSyncStatusSchema.optional(),
    syncedAt: z.string().nullable().optional(),
    oriCode: z.string().nullable().optional(),
    workstationName: z.string().nullable().optional(),
    applicantProfile: clientApplicationProfileSchema,
    client: applicationDetailClientSchema.optional(),
    invoice: applicationDetailInvoiceSchema.nullable().optional(),
    unreadMessageCount: z.number().optional(),
    fileNumber: z.string().nullable().optional(),
    trackingNumber: z.string().nullable().optional(),
    mailingDestination: z.string().nullable().optional(),
    mailingAddressOption: z.string().nullable().optional(),
    mailingCourierOption: z.string().nullable().optional(),
    mailingAddress: z
      .object({
        country: z.string(),
        unitApartment: z.string().nullable().optional(),
        street: z.string(),
        city: z.string(),
        stateProvince: z.string().nullable().optional(),
        zipPostalCode: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    internalNote: z.string().nullable().optional(),
    internalNotes: z.string().nullable().optional(),
  })
  .transform(({ unreadMessageCount, syncStatus, syncedAt, internalNote, internalNotes, ...rest }) => ({
    ...rest,
    syncStatus: syncStatus ?? APPLICATION_SYNC_STATUS.NOT_SYNCED,
    syncedAt: syncedAt ?? null,
    unreadMessageCount: unreadMessageCount ?? 0,
    internalNote: internalNote ?? internalNotes ?? null,
  }))

export type TClientApplicationDetail = z.infer<typeof clientApplicationDetailSchema>

export const clientListApplicationSchema = getPaginationDataSchema(clientApplicationDetailSchema)

export type TClientListApplication = z.infer<typeof clientListApplicationSchema>

export const clientApplicationDocumentFileSchema = clientFileSchema.extend({
  uploadedBy: z.string(),
  uploadedDate: z.string(),
  applicationDocumentId: z.number(),
  status: z.string(),
  approvedDate: z.string().nullable(),
  rejectedDate: z.string().nullable(),
  commentsCount: z.number().optional(),
  hasNewComment: z.boolean().optional(),
  isSigned: z.boolean().optional(),
  fileSize: z.number().optional(),
  signedFilePath: z.string().optional().nullable(),
})

export type TClientApplicationDocumentFile = z.infer<typeof clientApplicationDocumentFileSchema>

export const clientApplicationDocumentStatusSchema = z.enum([
  CLIENT_DOCUMENT_STATUS.APPROVED,
  CLIENT_DOCUMENT_STATUS.REJECTED,
  CLIENT_DOCUMENT_STATUS.UPLOAD_REQUIRED,
  CLIENT_DOCUMENT_STATUS.WAITING_APPROVAL,
  CLIENT_DOCUMENT_STATUS.IN_PROGRESS,
  CLIENT_DOCUMENT_STATUS.PENDING_APPROVAL,
  CLIENT_DOCUMENT_STATUS.PENDING_REJECTION,
])

export type TClientApplicationDocumentStatus = z.infer<typeof clientApplicationDocumentStatusSchema>

export const clientApplicationDocumentSchema = z.object({
  id: z.number(),
  createdDate: z.string(),
  applicationId: z.number(),
  documentTypeId: z.number(),
  documentTypeName: z.string(),
  updatedDate: z.string().nullable(),
  approvedDate: z.string().nullable(),
  rejectedDate: z.string().nullable(),
  submittedDate: z.string().nullable(),
  sampleFilePath: z.string().nullable(),
  templateFilePath: z.string().nullable(),
  status: clientApplicationDocumentStatusSchema,
  documentTypeDescription: z.string().nullable(),
  files: z.array(clientApplicationDocumentFileSchema),
  isOptional: z.boolean().optional(),
  displayUpload: z.boolean().optional(),
  displayTemplate: z.boolean().optional(),
  displaySample: z.boolean().optional(),
  displayDownload: z.boolean().optional(),
  requireESignature: z.boolean().optional(),
  requireUpload: z.boolean().optional(),
  staffUploadOnly: z.boolean().optional(),
  skipApproval: z.boolean().optional(),
  staffDocumentSection: z.string().nullable().optional(),
})

export type TClientApplicationDocument = z.infer<typeof clientApplicationDocumentSchema>

export const clientApplicationListDocumentsSchema = z.array(clientApplicationDocumentSchema)

export type TClientApplicationListDocuments = z.infer<typeof clientApplicationListDocumentsSchema>

/** API DTO (id, fileName, size, contentType) or internal fields; normalized to one shape. */
export const applicationChatAttachmentSchema = z
  .object({
    attachmentId: z.number().optional(),
    id: z.union([z.number(), z.string()]).optional(),
    attachmentName: z.string().nullable().optional(),
    fileName: z.string().nullable().optional(),
    attachmentSize: z.number().nullable().optional(),
    size: z.number().nullable().optional(),
    attachmentType: z.string().nullable().optional(),
    attachmentContentType: z.string().nullable().optional(),
    contentType: z.string().nullable().optional(),
    thumbnailUrl: z.string().nullable().optional(),
    ThumbnailUrl: z.string().nullable().optional(),
  })
  .transform((row) => {
    const rawId = row.attachmentId ?? row.id
    let attachmentId = 0
    if (typeof rawId === 'number' && !Number.isNaN(rawId)) attachmentId = rawId
    else if (typeof rawId === 'string' && rawId !== '') {
      const n = Number(rawId)
      attachmentId = Number.isNaN(n) ? 0 : n
    }
    const rawThumb = row.thumbnailUrl ?? row.ThumbnailUrl
    const thumbnailUrl =
      typeof rawThumb === 'string' && rawThumb.trim().length ? rawThumb.trim() : null
    return {
      attachmentId,
      attachmentName: row.attachmentName ?? row.fileName ?? null,
      attachmentSize: row.attachmentSize ?? row.size ?? null,
      attachmentType: row.attachmentType ?? null,
      attachmentContentType: row.attachmentContentType ?? row.contentType ?? null,
      thumbnailUrl,
    }
  })

export const applicationDetailChatMessageSchema = z.object({
  id: z.number(),
  message: z.string(),
  senderId: z.string(),
  senderName: z.string().nullable().optional(),
  createdAt: z.string(),
  senderType: z.string(),
  applicationId: z.number(),
  documentFileId: z.number().nullable().optional(),
  isClientMessage: z.boolean(),
  readAt: z.string().nullable(),
  readBy: z.string().nullable(),
  attachments: z.array(applicationChatAttachmentSchema).nullable().optional(),
})

export type TApplicationDetailChatMessage = z.infer<typeof applicationDetailChatMessageSchema>

export function createOptimisticApplicationDetailChatMessage(
  defaultValues: Partial<TApplicationDetailChatMessage> & { attachmentIds?: number[] } = {}
) {
  const { attachmentIds, ...rest } = defaultValues
  const derivedAttachments =
    rest.attachments ??
    (attachmentIds?.length
      ? attachmentIds.map((id) => ({
          attachmentId: id,
          attachmentName: null,
          attachmentSize: null,
          attachmentType: null,
          attachmentContentType: null,
          thumbnailUrl: null,
        }))
      : null)

  const tempId = rest.id ?? -Date.now()
  return {
    id: tempId,
    message: '',
    readAt: null,
    readBy: null,
    senderId: '',
    senderName: null,
    applicationId: 0,
    senderType: 'system',
    documentFileId: null,
    isClientMessage: true,
    createdAt: new Date().toISOString(),
    ...rest,
    attachments: derivedAttachments,
  } satisfies TApplicationDetailChatMessage
}

export const conversationItemSchema = z.object({
  applicationId: z.number(),
  otherPartyName: z.string(),
  lastMessageId: z.number(),
  lastMessagePreview: z.string(),
  lastMessageAt: z.string(),
  lastMessageSenderId: z.string(),
  unreadCount: z.number(),
  lastMessageAttachmentType: z.string().nullable(),
})

export type TConversationItem = z.infer<typeof conversationItemSchema>

export const conversationsResponseSchema = getPaginationDataSchema(conversationItemSchema)

export type TConversationsResponse = z.infer<typeof conversationsResponseSchema>

export const staffDocumentMessagePreviewSchema = z.object({
  content: z.string(),
})

export type TStaffDocumentMessagePreview = z.infer<typeof staffDocumentMessagePreviewSchema>

export const workstationSyncHistoryItemSchema = z.object({
  id: z.number(),
  applicationId: z.number(),
  oriCode: z.string(),
  syncedAt: z.string(),
  status: clientApplicationSyncStatusSchema,
  workstationName: z.string().nullable(),
})

export type TWorkstationSyncHistoryItem = z.infer<typeof workstationSyncHistoryItemSchema>

export const workstationSyncHistorySchema = z.array(workstationSyncHistoryItemSchema)
